import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { useAuthStore } from '@/store/authStore';
import type { ApiError } from '@/types';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// V1.1.0 (PWA): gửi kèm phiên bản app trong mọi request.
// PWA đã cài trên máy người dùng có thể chạy bản cũ (khác với web luôn tải mới).
const APP_VERSION = '1.1.0';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// ============================================================
// V1.1.0: Refresh token (phiên dài hạn cho thiết bị di động)
//
// Lưu ở localStorage — phải sống sót qua việc đóng/mở lại app trên điện thoại,
// nên không thể giữ trong bộ nhớ.
// ============================================================
const REFRESH_KEY = 'hv_refresh_token';

export const refreshTokenStore = {
  get: () => localStorage.getItem(REFRESH_KEY),
  set: (t: string) => localStorage.setItem(REFRESH_KEY, t),
  clear: () => localStorage.removeItem(REFRESH_KEY),
};

/** Gọi NGAY SAU khi login thành công (trên thiết bị di động) để lấy phiên dài hạn. */
export async function startDeviceSession(deviceLabel?: string): Promise<void> {
  const res = await apiClient.post<{ token: string; refreshToken: string }>(
    '/auth/device-session',
    { deviceLabel: deviceLabel ?? navigator.userAgent.slice(0, 120) },
  );
  refreshTokenStore.set(res.data.refreshToken);
}

// ============================================================
// Request interceptor: inject JWT + phiên bản app
// ============================================================
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.headers) {
    config.headers['X-App-Version'] = APP_VERSION;
  }
  return config;
});

// ============================================================
// Response interceptor
//
// 401 xử lý theo 2 nhánh:
//   - CÓ refresh token  → gia hạn ngầm rồi phát lại request. Người dùng không thấy gián đoạn.
//   - KHÔNG có          → GIỮ NGUYÊN hành vi cũ (đăng xuất, về /login).
//     → Web hiện tại không bật phiên dài hạn nên hoạt động y như trước. Không rủi ro.
//
// Chống bão request: nhiều request 401 cùng lúc chỉ gọi refresh MỘT lần,
// các request còn lại chờ chung kết quả.
// ============================================================
let refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const rt = refreshTokenStore.get();
  if (!rt) return null;

  try {
    // Dùng axios "trần", KHÔNG qua apiClient — tránh đệ quy vô hạn nếu chính
    // lời gọi refresh cũng trả 401.
    const res = await axios.post<{
      token: string;
      expiresAt: string;
      refreshToken: string;
      refreshExpiresAt: string;
    }>(
      `${baseURL}/auth/refresh`,
      { refreshToken: rt },
      { headers: { 'Content-Type': 'application/json' } },
    );

    // Rotating: server đã thu hồi token cũ → bắt buộc lưu token mới.
    refreshTokenStore.set(res.data.refreshToken);
    useAuthStore.getState().setToken(res.data.token);
    return res.data.token;
  } catch {
    refreshTokenStore.clear();
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401: {
          const isRefreshCall = original?.url?.includes('/auth/refresh');

          if (original && !original._retried && !isRefreshCall && refreshTokenStore.get()) {
            original._retried = true;

            refreshing ??= doRefresh().finally(() => { refreshing = null; });
            const newToken = await refreshing;

            if (newToken) {
              original.headers = original.headers ?? {};
              (original.headers as Record<string, unknown>).Authorization = `Bearer ${newToken}`;
              return apiClient.request(original);
            }
          }

          // Hành vi cũ — giữ nguyên
          refreshTokenStore.clear();
          useAuthStore.getState().logout();
          message.error('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại');
          window.location.href = '/login';
          break;
        }
        case 403:
          message.error('Bạn không có quyền thực hiện thao tác này');
          break;
        case 404:
          // Không toast — component tự xử lý
          break;
        case 422:
          message.error(data?.message ?? 'Dữ liệu không hợp lệ');
          break;
        case 500:
        case 502:
        case 503:
          message.error('Lỗi hệ thống, vui lòng thử lại sau');
          break;
        default:
          message.error(data?.message ?? 'Đã có lỗi xảy ra');
      }
    } else if (error.request) {
      message.error('Không kết nối được tới máy chủ');
    }
    return Promise.reject(error);
  },
);
